'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn, staggerContainer } from '@/lib/animations';
import { 
  Warehouse, 
  Truck, 
  BarChart3, 
  Package, 
  Map, 
  Sparkles 
} from 'lucide-react';

const services = [
  {
    title: 'Warehousing Solutions',
    description: 'Secure, climate-controlled warehousing with inventory management systems to optimize your storage needs.',
    icon: Warehouse,
  },
  {
    title: 'Transportation Management',
    description: 'Streamlined transportation services with real-time tracking and route optimization for efficient delivery.',
    icon: Truck,
  },
  {
    title: 'Supply Chain Analytics',
    description: 'Data-driven insights to identify inefficiencies and opportunities for improvement in your supply chain.',
    icon: BarChart3,
  },
  {
    title: 'Order Fulfillment',
    description: 'Fast, accurate order processing and fulfillment services with quality control checks.',
    icon: Package,
  },
  {
    title: 'Distribution Network Design',
    description: 'Strategic planning and implementation of distribution networks to optimize your market reach.',
    icon: Map,
  },
  {
    title: 'Value-Added Services',
    description: 'Custom kitting, assembly, labeling, and other specialized services to enhance your product offerings.',
    icon: Sparkles,
  },
];

export function Services() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
    rootMargin: '200px 0px',
  });

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">Our Services</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Wisconsin 3PL Services Include
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            We offer a full range of third-party logistics services designed to optimize your supply chain and drive business growth.
          </p>
        </motion.div>
        
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeIn('up', 0.1 * index)}
              className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-6 text-brand-600 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <service.icon size={24} />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h4>
              <p className="text-gray-700">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}